'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import { AlertType } from '@alltrails/shared/types/alert';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Caution from '@alltrails/denali/icons/Caution';
import ChevronSmall from '@alltrails/denali/icons/ChevronSm';
import Button from '@alltrails/denali/components/Button';
import type { TrailInfo } from '@alltrails/di-tooling';
import type { Group } from '@alltrails/groups';
import isClosureOrDangerAlertActive from '../../utils/isClosureOrDangerAlertActive';
import styles from './AlertsButton.module.scss';

const AlertsModal = dynamic(() => import('@/components/AlertsModal/AlertsModal'), { ssr: false });

type AlertsButtonProps = {
  className?: string;
  isAdmin?: boolean;
  isPLPUser?: boolean;
  trailDetails: TrailDetails;
  trailInfo?: TrailInfo;
  userGroups?: Group[];
};

const AlertsButton = ({ className, isAdmin, isPLPUser, trailDetails, trailInfo, userGroups }: AlertsButtonProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);

  const hasAlerts =
    trailDetails.alerts &&
    trailDetails.alerts.length > 0 &&
    trailDetails.alerts.every(
      alert => alert.alertType === AlertType.INFORMATION || alert.alertType === AlertType.WARNING || !isClosureOrDangerAlertActive(alert)
    );

  if (!hasAlerts) {
    return null;
  }

  const onAlertsButtonClick = () => {
    setIsAlertsModalOpen(true);
    logTrailDetailsOverviewLinkClicked({ overview_link_clicked: 'alert', trail_id: trailDetails.id });
  };

  const alertsButtonText = intl.formatMessage(
    { defaultMessage: '{count, plural, one {# alert} other {# alerts}}' },
    { count: trailDetails.alerts?.length }
  );
  return (
    <div className={className}>
      {isMobile ? (
        <button className={styles.mobileAlertsButton} onClick={onAlertsButtonClick}>
          <div className={styles.icon}>
            <Caution />
          </div>
          {alertsButtonText}
          <ChevronSmall className={styles.chevron} orientation="right" />
        </button>
      ) : (
        <Button icon={{ Component: Caution }} onClick={onAlertsButtonClick} testId="alerts" text={alertsButtonText} />
      )}
      {isAlertsModalOpen && (
        <AlertsModal
          isAdmin={isAdmin}
          isPLPUser={isPLPUser}
          onClose={() => setIsAlertsModalOpen(false)}
          trailDetails={trailDetails}
          trailInfo={trailInfo}
          userGroups={userGroups}
        />
      )}
    </div>
  );
};

export default AlertsButton;
